const DEFAULT_RECORDING_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
] as const;

export interface RecordedAudioNote {
  file: File;
  durationMs: number;
}

export function canRecordAudio(): boolean {
  return typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';
}

export class AudioNoteRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;

  async start(): Promise<void> {
    if (!canRecordAudio()) {
      throw new Error('Audio recording is not supported in this browser.');
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    this.startedAt = Date.now();

    const mimeType = this.pickMimeType();
    // TODO(audio-notes-ui): add waveform / level-meter support by exposing the
    // MediaStream or an AnalyserNode alongside this recorder lifecycle.
    this.mediaRecorder = mimeType
      ? new MediaRecorder(this.mediaStream, { mimeType })
      : new MediaRecorder(this.mediaStream);

    this.mediaRecorder.addEventListener('dataavailable', this.handleDataAvailable);
    this.mediaRecorder.start();
  }

  async stop(): Promise<RecordedAudioNote> {
    const recorder = this.mediaRecorder;
    if (!recorder) {
      throw new Error('Recording has not been started.');
    }

    return new Promise<RecordedAudioNote>((resolve, reject) => {
      const cleanup = () => {
        recorder.removeEventListener('dataavailable', this.handleDataAvailable);
        recorder.removeEventListener('stop', handleStop);
        recorder.removeEventListener('error', handleError);
        this.mediaStream?.getTracks().forEach((track) => track.stop());
        this.mediaRecorder = null;
        this.mediaStream = null;
      };

      const handleStop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const extension = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(this.chunks, { type: mimeType });
        cleanup();
        resolve({
          file: new File([blob], `voice-note.${extension}`, { type: mimeType }),
          durationMs: Date.now() - this.startedAt,
        });
      };

      const handleError = () => {
        cleanup();
        reject(new Error('Recording failed.'));
      };

      recorder.addEventListener('stop', handleStop, { once: true });
      recorder.addEventListener('error', handleError, { once: true });
      recorder.stop();
    });
  }

  cancel(): void {
    this.mediaRecorder?.stop();
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.chunks = [];
  }

  private handleDataAvailable = (event: BlobEvent) => {
    if (event.data.size > 0) {
      this.chunks.push(event.data);
    }
  };

  private pickMimeType(): string | undefined {
    return DEFAULT_RECORDING_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
  }
}
